import {useAmplitude, useUser} from "../../../contexts";
import {useCancelPaymentPlanAutoPay} from "../../../apis/requests/backoffice/cancelPaymentPlanAutoPay";
import {useGetDashboardQuery} from "../../../apis/requests/plans";
import {useCallback, useMemo} from "react";
import NiceModal from "@ebay/nice-modal-react";
import {
  ChangePaymentMethod,
  DelayPaymentDialogResult,
  EnableAutoPayDialog,
  OneTimePaymentDialog,
  PreCancelPlanDialog,
} from "../../../layout/account/menu/dialogs";
import AreYouSureDialog from "../../../pages/backoffice/information-panel/components/dialogs/AreYouSureDialog";
import {useAccountId} from "../../../contexts/useAccountId";
import {useGetAccount} from "../../../apis/requests/user/getAccount";
import {Pages} from "../../../bi-events/enum";
import {PlanBuilderDialog} from "../../../pages/backoffice/information-panel/tabs/payment-plan-tab/components/plan-builder-view/PlanBuilderDialog";

interface Props {
  onClose: () => void;
}

export const useDashboardMenuContent = ({onClose}: Props) => {
  const accountId = useAccountId();
  const {data: account} = useGetAccount({accountId}, {enabled: !!accountId});
  const activePaymentPlan = useMemo(() => account?.active_payment_plan, [account]);
  const {isAgent} = useUser();
  const {pageClickEvent} = useAmplitude();
  const cancelAutoPay = useCancelPaymentPlanAutoPay();

  const activePaymentPlanId = activePaymentPlan?.payment_plan_id;
  const userDashboard = useGetDashboardQuery(
    {
      account_id: accountId,
      isAgent,
      payment_plan_id: activePaymentPlanId,
    },
    {enabled: !!activePaymentPlanId}
  );
  const frequency = useMemo(() => {
    if (userDashboard.data) {
      return userDashboard.data.installment_frequency;
    }
  }, [userDashboard.data]);
  const pendingAmount = userDashboard?.data?.pending_amount || 0;
  const planBalance = userDashboard.data?.amount_unpaid || 0;
  const autoPayEnrolled = useMemo(() => {
    if (userDashboard.data) {
      return userDashboard.data.autopay_enrolled;
    }
  }, [userDashboard.data]);
  const isAccountResolved = activePaymentPlan?.state === "resolved";

  const shouldDisplayDashboardMenu = Boolean(activePaymentPlan) && !isAccountResolved;
  const shouldDisplayCancelMyPlan = Boolean(frequency);
  const shouldDisplaySwitchToAutoPay = !autoPayEnrolled;
  const shouldDisplayCancelAutoPay = isAgent && autoPayEnrolled;
  const shouldDisplayPreviewPlans = isAgent;

  const onOneTimePaymentClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "one_time_payment"});
    onClose();
    await NiceModal.show<boolean>(OneTimePaymentDialog, {
      planBalance,
      accountId,
      activePaymentPlanId,
      pendingAmount,
    });
  }, [onClose, planBalance, accountId, activePaymentPlanId, pendingAmount]);

  const onPaymentMethodChangeClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "payment_method"});
    onClose();
    await NiceModal.show<boolean>(ChangePaymentMethod, {
      accountId,
      title: "Manage Payment Method",
      activePaymentPlanId,
    });
  }, [onClose, accountId, activePaymentPlanId]);

  const onCancelMyPlanClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "cancel_plan"});
    onClose();
    await NiceModal.show<DelayPaymentDialogResult>(PreCancelPlanDialog, {
      accountId,
      frequency,
      activePaymentPlanId,
    });
  }, [onClose, accountId, frequency, activePaymentPlanId]);

  const onSwitchToAutoPayClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "switch_autopay"});
    try {
      onClose();
      await NiceModal.show<boolean>(EnableAutoPayDialog, {
        onEnableAutoPay: async () => {
          await NiceModal.show<boolean>(ChangePaymentMethod, {
            accountId,
            title: "Add Payment Method for AutoPay",
            activePaymentPlanId,
          });
        },
      });
    } catch {}
  }, [onClose, accountId, activePaymentPlanId]);

  const onCancelAutoPayClick = useCallback(async () => {
    pageClickEvent({page: Pages.MENU, cta: "cancel_auto_pay"});
    onClose();
    await NiceModal.show(AreYouSureDialog, {
      onConfirm: async () => {
        await cancelAutoPay.mutateAsync({
          accountId,
          paymentPlanId: activePaymentPlanId!,
        });
      },
      contentTextId: "Are you sure you want to cancel auto pay?",
      confirmTextId: "Continue",
    });
  }, [onClose, accountId, activePaymentPlanId]);

  const onDisplayPreviewPlans = useCallback(async () => {
    onClose();
    await NiceModal.show(PlanBuilderDialog, {});
  }, [onClose]);

  return {
    shouldDisplayDashboardMenu,
    shouldDisplayCancelMyPlan,
    shouldDisplayCancelAutoPay,
    shouldDisplaySwitchToAutoPay,
    shouldDisplayPreviewPlans,
    onOneTimePaymentClick,
    onPaymentMethodChangeClick,
    onCancelMyPlanClick,
    onSwitchToAutoPayClick,
    onCancelAutoPayClick,
    onDisplayPreviewPlans,
  };
};
